<template>
    <div
        :id="`process-element-${element.id}`"
        :style="containerStyles"
        class="process-element-overlay"
        ref="processElementOverlay"
        v-if="elementType !== ProcessElementType.FLOW"
    >
        <!-- Always render the top level div, since it defines the width -->
        <Popup
            v-if="!isEditing && currentElement && elementType"
            @mouseenter="handleMouseEnter"
            @mouseleave="handleMouseLeave"
            :anchor="processElementOverlayElement!"
        >
            <GenericCard
                :entityId="currentElement.identity"
                :entityType="PROCESS_ELEMENT_ENTITY_TYPE_MAP[elementType]"
            />
        </Popup>
        <RouterLink
            :id="`process-element-link-${businessObject.taskId}`"
            class="process-element-link"
            :class="{
                hover: isHovering,
            }"
            :to="routerLink"
            :aria-label="currentElement?.name ?? businessObject.name"
        />
    </div>
</template>

<script setup lang="ts">
import { computed, ref, toRef, useTemplateRef } from "vue";
import {
    ProcessElement,
    useProcessElementsManager,
} from "@/stores/process-elements-store";
import GenericCard from "@/components/EntityInfo/GenericCard.vue";
import Popup from "@/components/Popup.vue";
import { type Element as BpmnElement } from "bpmn-js/lib/model/Types";
import { useBpmnManager } from "@/composables/bpmn-manager";
import { getBusinessObject } from "bpmn-js/lib/util/ModelUtil";
import { useEditManager } from "@/composables/edit-manager";
import { useRoute } from "vue-router";
import { EntityType } from "@/stores/entity-store";
import { ProcessElementType } from "@/lib/openapi/gen";
import {
    elementIsEvent,
    elementIsGateway,
    elementIsMessageFlow,
    elementIsSequenceFlow,
    elementIsTask,
} from "@/composables/bpmn-manager/helpers";

const props = defineProps<{
    element: BpmnElement;
    bpmnManager: ReturnType<typeof useBpmnManager>;
}>();

const element = toRef(props.element);
const { processElements } = useProcessElementsManager();
const { isEditing } = useEditManager();
const isHovering = ref(false);
const route = useRoute();
const processElementOverlayElement = useTemplateRef("processElementOverlay");
const businessObject = computed(() => getBusinessObject(element.value));

const containerStyles = computed(() => {
    // Add padding to the element container to overlay BPMN element border
    return {
        "width": element.value.width + 16 + "px",
        "height": element.value.height + 16 + "px",
    };
});

/**
 * Get the current element from the processElements list.
 */
const currentElement = computed<ProcessElement | undefined>(() => {
    const processElementId = getBusinessObject(element.value).processElementId;
    return processElements.value?.find((t) => t.identity === processElementId);
});

const PROCESS_ELEMENT_ENTITY_TYPE_MAP = {
    [ProcessElementType.TASK]: EntityType.TASK,
    [ProcessElementType.EVENT]: EntityType.EVENT,
    [ProcessElementType.GATEWAY]: EntityType.GATEWAY,
    [ProcessElementType.FLOW]: EntityType.FLOW,
};

const elementType = computed(() => {
    if (elementIsTask(element.value)) {
        return ProcessElementType.TASK;
    } else if (elementIsEvent(element.value)) {
        return ProcessElementType.EVENT;
    } else if (elementIsGateway(element.value)) {
        return ProcessElementType.GATEWAY;
    } else if (
        elementIsMessageFlow(element.value) ||
        elementIsSequenceFlow(element.value)
    ) {
        return ProcessElementType.FLOW;
    }
});

const routerLink = computed(() => {
    const baseParams = {
        orgName: route.params.orgName,
        stekName: route.params.stekName,
    };

    switch (elementType.value) {
        case ProcessElementType.TASK:
            return {
                name: "taskPage",
                params: {
                    ...baseParams,
                    taskId: getBusinessObject(element.value).processElementId,
                },
            };
        case ProcessElementType.EVENT:
            return {
                name: "eventPage",
                params: {
                    ...baseParams,
                    eventId: getBusinessObject(element.value).processElementId,
                },
            };
        case ProcessElementType.GATEWAY:
            return {
                name: "gatewayPage",
                params: {
                    ...baseParams,
                    gatewayId: getBusinessObject(element.value)
                        .processElementId,
                },
            };
        case ProcessElementType.FLOW:
            return {
                name: "flowPage",
                params: {
                    ...baseParams,
                    flowId: getBusinessObject(element.value).processElementId,
                },
            };
        default:
            return {};
    }
});

const handleMouseEnter = () => {
    isHovering.value = true;
    props.bpmnManager.eventBus?.fire("element.hover", {
        element: element.value,
        hover: true,
    });
};

const handleMouseLeave = () => {
    isHovering.value = false;
    props.bpmnManager.eventBus?.fire("element.out", {
        element: element.value,
        hover: false,
    });
};
</script>

<style scoped lang="scss">
.process-element-link {
    text-decoration: none;
    padding: 8px;
    text-align: center;
    display: flex;
    flex-direction: column;
    gap: 0.25em;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;

    // Give the text same properties as the BPMN text.
    color: black;
    font-family: Arial, sans-serif;
    line-height: 1.2;
    font-size: 12px;

    &.hover {
        color: white;
    }

    * {
        max-width: 90%;
        text-wrap-mode: wrap;
        overflow-wrap: break-word;
    }
}
</style>
